import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Stack,
  TextField,
  IconButton,
  Typography,
  Card,
  CardContent,
  Chip,
  alpha,
  CircularProgress,
  Popover,
  Slider,
  styled,
  Tooltip,
  Container,
  Divider,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import {
  CleanInputCard,
  CleanIconButton,
  GradientLoadingText,
  TopLoader,
} from "./saanvi-styles";
import { API_CONFIG } from "./saanvi-constants";
import ReactMarkdown from "react-markdown";

const IOSSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.primary.main,
  height: 8,
  padding: "15px 0",
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: "#fff",
    boxShadow:
      "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)",
    "&:focus, &:hover, &.Mui-active": {
      boxShadow:
        "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)",
    },
  },
  "& .MuiSlider-valueLabel": {
    fontSize: 12,
    fontWeight: "normal",
    top: -6,
    backgroundColor: "unset",
    color: theme.palette.text.primary,
    "&:before": { display: "none" },
  },
  "& .MuiSlider-track": { border: "none", height: 8, borderRadius: 4 },
  "& .MuiSlider-rail": {
    opacity: 0.3,
    backgroundColor: "#bfbfbf",
    height: 8,
    borderRadius: 4,
  },
}));

function PageCard({ result }) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "20px",
        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          transform: "translateY(-4px)",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
        },
      }}
    >
      <CardContent
        sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Stack spacing={2.5} sx={{ height: "100%" }}>
          {/* Title with icon */}
          <Box
            component="a"
            href={result.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              window.open(result.link, "_blank", "noopener,noreferrer");
            }}
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: "17px",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              lineHeight: 1.4,
              "&:hover": { color: "primary.dark" },
              cursor: "pointer",
              transition: "color 0.2s",
            }}
          >
            <Box sx={{ flex: 1 }}>{result.title}</Box>
            <OpenInNewIcon
              sx={{ fontSize: 16, flexShrink: 0, mt: 0.3, opacity: 0.5 }}
            />
          </Box>

          {/* Summary with scrollbar */}
          <Box
            sx={{
              flex: 1,
              maxHeight: 240,
              overflowY: "auto",
              pr: 1,
              "&::-webkit-scrollbar": { width: "5px" },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: (theme) => alpha(theme.palette.text.secondary, 0.2),
                borderRadius: "10px",
                "&:hover": {
                  background: (theme) =>
                    alpha(theme.palette.text.secondary, 0.4),
                },
              },
            }}
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "14px",
                      lineHeight: 1.7,
                      mb: 1.5,
                    }}
                  >
                    {children}
                  </Typography>
                ),
                strong: ({ children }) => (
                  <strong style={{ fontWeight: 600, color: "inherit" }}>
                    {children}
                  </strong>
                ),
                em: ({ children }) => <em>{children}</em>,
                ul: ({ children }) => (
                  <Box component="ul" sx={{ pl: 2.5, my: 1 }}>
                    {children}
                  </Box>
                ),
                ol: ({ children }) => (
                  <Box component="ol" sx={{ pl: 2.5, my: 1 }}>
                    {children}
                  </Box>
                ),
                li: ({ children }) => (
                  <Typography
                    component="li"
                    variant="body2"
                    sx={{ color: "text.secondary", fontSize: "14px", mb: 0.5 }}
                  >
                    {children}
                  </Typography>
                ),
                code: ({ children }) => (
                  <Box
                    component="code"
                    sx={{
                      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: "13px",
                      fontFamily: "monospace",
                    }}
                  >
                    {children}
                  </Box>
                ),
              }}
            >
              {result.summary}
            </ReactMarkdown>
          </Box>

          {/* Metadata chips */}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: "auto" }}
          >
            {result.wordCount && (
              <Chip
                label={`${result.wordCount} words`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "11px",
                  fontWeight: 500,
                  background: (theme) => alpha(theme.palette.info.main, 0.08),
                  color: "info.main",
                  border: "none",
                }}
              />
            )}
            {result.publishedAt && (
              <Chip
                label={result.publishedAt}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "11px",
                  fontWeight: 500,
                  background: (theme) =>
                    alpha(theme.palette.text.secondary, 0.08),
                  color: "text.secondary",
                  border: "none",
                }}
              />
            )}
            {result.author && (
              <Chip
                label={result.author}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "11px",
                  fontWeight: 500,
                  background: (theme) =>
                    alpha(theme.palette.success.main, 0.08),
                  color: "success.main",
                  border: "none",
                }}
              />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ArcSearch({ authToken, onResultsChange }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [thinkingCapacity, setThinkingCapacity] = useState(5);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const abortControllerRef = useRef(null);

  const handleSearch = useCallback(
    async (e) => {
      e?.preventDefault();
      if (!query.trim() || loading) return;

      setLoading(true);
      setQuestions([]);
      setCurrentQuestion("");

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        const response = await fetch(`${API_CONFIG.baseUrl}/search`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: authToken ? `Bearer ${authToken}` : "",
          },
          body: JSON.stringify({
            query: query.trim(),
            maxIterations: thinkingCapacity,
            resultsPerQuery: 6,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;

            const dataStr = line.slice(6);
            if (dataStr === "[DONE]") continue;

            try {
              const event = JSON.parse(dataStr);

              if (event.type === "question") {
                setCurrentQuestion(event.question);
                setQuestions((prev) => [
                  ...prev,
                  { question: event.question, sources: [] },
                ]);
              } else if (event.type === "source") {
                setQuestions((prev) => {
                  if (prev.length === 0) return prev;
                  const updated = [...prev];
                  updated[updated.length - 1].sources.push(event.source);
                  return updated;
                });
              } else if (event.type === "complete") {
                setCurrentQuestion("");
                setLoading(false);
              } else if (event.type === "error") {
                console.error("Stream error:", event.message);
                setLoading(false);
              }
            } catch (err) {
              console.error("Failed to parse SSE event:", err);
            }
          }
        }
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("Search aborted");
        } else {
          console.error("Arc Search error:", error);
        }
        setLoading(false);
        setCurrentQuestion("");
      }
    },
    [query, loading, authToken, thinkingCapacity]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSearch(e);
      }
    },
    [handleSearch]
  );

  useEffect(() => {
    onResultsChange?.(questions.length > 0);
  }, [questions.length, onResultsChange]);

  return (
    <Box
      sx={{
        height: "75dvh",
        overflow: "auto",
        position: "relative",
        "&::-webkit-scrollbar": { width: "8px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          background: (theme) => alpha(theme.palette.text.secondary, 0.2),
          borderRadius: "10px",
          "&:hover": {
            background: (theme) => alpha(theme.palette.text.secondary, 0.4),
          },
        },
      }}
    >
      {/* Top Loader Overlay */}
      {loading && <TopLoader />}

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Stack spacing={4}>
          {/* Search Box */}
          <Box
            sx={{
              maxWidth: 800,
              mx: "auto",
              width: "100%",
              position: "sticky",
              top: 0,
              zIndex: 99,
              background: "black",
              py: 2,
              boxShadow: "2px 2px 12px 1px black",
            }}
          >
            <CleanInputCard sx={{ boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
              <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                <form onSubmit={handleSearch}>
                  <TextField
                    fullWidth
                    placeholder="Search the web for detailed information..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: "16px", fontWeight: 500 },
                      endAdornment: (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Tooltip
                            title={`Thinking Capacity: ${thinkingCapacity}`}
                          >
                            <IconButton
                              onClick={(e) =>
                                setSettingsAnchor(e.currentTarget)
                              }
                              disabled={loading}
                              size="small"
                              sx={{
                                color: "text.secondary",
                                "&:hover": {
                                  color: "primary.main",
                                  backgroundColor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.08),
                                },
                              }}
                            >
                              <TuneRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <CleanIconButton
                            variant="send"
                            type="submit"
                            disabled={!query.trim() || loading}
                          >
                            <SearchIcon sx={{ fontSize: 20 }} />
                          </CleanIconButton>
                        </Stack>
                      ),
                    }}
                  />
                </form>
              </CardContent>
            </CleanInputCard>
          </Box>

          {/* Settings Popover */}
          <Popover
            open={Boolean(settingsAnchor)}
            anchorEl={settingsAnchor}
            onClose={() => setSettingsAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                p: 3,
                width: 340,
                borderRadius: "24px",
                boxShadow: "0 20px 60px -12px rgba(0,0,0,0.25)",
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
              },
            }}
          >
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Thinking Capacity
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.5 }}
                >
                  Controls recursive depth. Higher values = deeper research.
                </Typography>
              </Box>
              <Box sx={{ px: 1 }}>
                <IOSSlider
                  value={thinkingCapacity}
                  onChange={(e, val) => setThinkingCapacity(val)}
                  min={1}
                  max={20}
                  step={1}
                  valueLabelDisplay="on"
                />
                <Stack direction="row" justifyContent="space-between" mt={1}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.secondary"
                  >
                    Fast (1)
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.secondary"
                  >
                    Deep (20)
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Popover>

          {/* Skeleton Placeholders while loading */}
          {loading && questions.length === 0 && (
            <Stack spacing={6}>
              {[1].map((idx) => (
                <Box key={idx}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={40}
                    sx={{ mb: 3 }}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                      gap: 3,
                    }}
                  >
                    {[1, 2].map((cardIdx) => (
                      <Card
                        key={cardIdx}
                        sx={{
                          borderRadius: "20px",
                          border: (theme) =>
                            `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Skeleton variant="text" width="80%" height={30} />
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={150}
                            sx={{ my: 2, borderRadius: 2 }}
                          />
                          <Stack direction="row" spacing={1}>
                            <Skeleton
                              variant="rounded"
                              width={80}
                              height={22}
                            />
                            <Skeleton
                              variant="rounded"
                              width={100}
                              height={22}
                            />
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
          )}

          {/* Results */}
          {questions.length > 0 && (
            <Stack spacing={6}>
              {questions.map((questionData, qIdx) => {
                const isActiveQuestion =
                  loading && qIdx === questions.length - 1;

                return (
                  <Box key={qIdx}>
                    {/* Question Header - Highlighted if active */}
                    <Box
                      sx={{
                        mb: 3,
                        py: 2,
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        color={
                          isActiveQuestion ? "primary.main" : "text.primary"
                        }
                        sx={{ fontSize: "18px", lineHeight: 1.5 }}
                      >
                        {questionData.question}
                      </Typography>
                    </Box>

                    {/* Sources Grid */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(2, 1fr)",
                        },
                        gap: 3,
                      }}
                    >
                      {/* Actual sources */}
                      {questionData.sources.map((result, idx) => (
                        <PageCard key={idx} result={result} />
                      ))}

                      {/* Loading placeholders for active question */}
                      {isActiveQuestion &&
                        Array.from({
                          length: Math.min(4 - questionData.sources.length, 2),
                        }).map((_, idx) => (
                          <Card
                            key={`placeholder-${idx}`}
                            sx={{
                              height: "100%",
                              borderRadius: "20px",
                              border: (theme) =>
                                `1px dashed ${alpha(
                                  theme.palette.divider,
                                  0.2
                                )}`,
                              bgcolor: (theme) =>
                                alpha(theme.palette.grey[500], 0.02),
                            }}
                          >
                            <CardContent sx={{ p: 3, height: "100%" }}>
                              <Stack spacing={2.5} sx={{ height: "100%" }}>
                                <Skeleton
                                  variant="text"
                                  width="85%"
                                  height={30}
                                />
                                <Skeleton
                                  variant="rectangular"
                                  width="100%"
                                  height={180}
                                  sx={{ borderRadius: 2 }}
                                />
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  sx={{ mt: "auto" }}
                                >
                                  <Skeleton
                                    variant="rounded"
                                    width={80}
                                    height={22}
                                  />
                                  <Skeleton
                                    variant="rounded"
                                    width={100}
                                    height={22}
                                  />
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}

          {/* Initial loading state (no questions yet) */}
          {loading && questions.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                px: 3,
                mx: "auto",
                maxWidth: 600,
                borderRadius: 3,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                borderLeft: (theme) =>
                  `4px solid ${theme.palette.primary.main}`,
              }}
            >
              <Typography
                variant="body1"
                color="primary.main"
                fontWeight={600}
                sx={{ mb: 2 }}
              >
                Analyzing your query and generating research questions...
              </Typography>
              <Stack spacing={2} alignItems="center">
                <Skeleton variant="text" width="70%" height={24} />
                <Skeleton variant="text" width="60%" height={24} />
              </Stack>
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
